/**
 * T-22 · El registro de módulos: qué se carga y cuándo.
 *
 * Cada módulo tiene **un `import()` literal**, y ésa es la pieza entera.
 *
 * > [!danger] Nunca una ruta variable
 * > ```ts
 * > import(`./${clave}/Pantalla.js`)   // ❌
 * > ```
 * > Parece lo mismo y hace lo contrario: el empaquetador no puede saber qué vale
 * > `clave`, así que **incluye todos los archivos que encajen** y los deja
 * > disponibles. El código se parte en fragmentos, sí — y el navegador se los
 * > baja todos igual. La separación se ve en el manifiesto y no existe en la red.
 * >
 * > Con rutas literales, el empaquetador ve exactamente dos destinos y sólo se
 * > pide el que se usa. `T-23` lo comprueba en cada corrida.
 *
 * ── La carpeta se llama como la CLAVE del catálogo ──────────────────────────
 * `loyalty`, no `lealtad`. Es lo que permite a `T-23` saber qué fragmento
 * pertenece a qué módulo **sin una segunda tabla que traducir** — y una segunda
 * tabla es una que se desincroniza. El nombre en español vive en `modules.name`,
 * que es lo que ve una persona.
 *
 * Lo cazó la comprobación el primer día que corrió: la carpeta era `lealtad` y
 * `loyalty` se quedó sin fragmento reconocible.
 *
 * ── Añadir un módulo obliga a añadir su pantalla ────────────────────────────
 * El tipo es `Record<Modulo, …>`, no un objeto suelto: el día que `T-17` sume un
 * tercer módulo al catálogo, **esto deja de compilar** hasta que tenga su
 * entrada. Un mapa parcial habría fallado en el navegador, con una pantalla en
 * blanco y sin decir por qué.
 */
import { lazy } from 'react'
import { conRecargaUnica } from './recarga.js'
import type { ComponentType, LazyExoticComponent } from 'react'
import type { Modulo, EstadoModulo } from '@fialrest/shared'

export interface PropsDeModulo { readonly estado: EstadoModulo }

export type PantallaDeModulo = LazyExoticComponent<ComponentType<PropsDeModulo>>

/**
 * `conRecargaUnica` envuelve la carga, no la sustituye: el `import()` de dentro
 * **sigue siendo literal**, que es lo único que el empaquetador mira. `T-23`
 * comprueba en cada corrida que la separación sobrevive a este envoltorio.
 */
export const REGISTRO: Record<Modulo, PantallaDeModulo> = {
  menu:    lazy(conRecargaUnica(() => import('./menu/Pantalla.js'))),
  loyalty: lazy(conRecargaUnica(() => import('./loyalty/Pantalla.js'))),
}

/**
 * `T-37` · **Fotografías**, la segunda pantalla del módulo `menu`.
 *
 * No entra en `REGISTRO` porque `REGISTRO` contesta a «¿cuál es la pantalla de
 * este módulo?» y su tipo `Record<Modulo, …>` es justo lo que obliga a que un
 * módulo nuevo traiga la suya. Esto es otra pregunta: **una segunda vista del
 * mismo módulo**. Meterla ahí habría convertido el mapa en uno de rutas, y el
 * día que `T-17` sume un tercer módulo dejaría de fallar la compilación por lo
 * que tiene que fallar.
 *
 * Va aquí igualmente, y no suelta en `main.tsx`, por una razón concreta: **todo
 * `import()` de una pantalla de módulo vive en este archivo**, que es lo que
 * `T-23` sabe mirar. Sigue siendo literal, sigue cayendo bajo
 * `src/modulos/menu/`, y por tanto sigue siendo código que sólo se descarga
 * quien contrató el menú — `scripts/ci/verificar-fragmentos.mjs` lo comprueba.
 */
export const PANTALLA_DE_FOTOGRAFIAS: PantallaDeModulo =
  lazy(conRecargaUnica(() => import('./menu/Fotografias.js')))

/**
 * `T-41` · **El catálogo de extras**, la tercera pantalla del módulo `menu`.
 *
 * Mismo argumento que `PANTALLA_DE_FOTOGRAFIAS`, letra por letra: no entra en
 * `REGISTRO` porque aquél contesta «¿cuál es la pantalla de este módulo?» y su
 * tipo `Record<Modulo, …>` es lo que obliga a que un módulo nuevo traiga la suya.
 * Y vive aquí y no suelta en `main.tsx` porque **todo `import()` de una pantalla
 * de módulo vive en este archivo**, que es lo que `T-23` sabe mirar — sigue
 * siendo literal, sigue cayendo bajo `src/modulos/menu/`, y por tanto sigue
 * siendo código que sólo se descarga quien contrató el menú.
 */
export const PANTALLA_DE_EXTRAS: PantallaDeModulo =
  lazy(conRecargaUnica(() => import('./menu/CatalogoDeExtras.js')))

/**
 * `T-43` · **Las plantillas de tamaños**, la cuarta. Mismo argumento que las
 * dos de arriba, y por tanto misma casa: el `import()` vive aquí para que siga
 * siendo literal y siga cayendo bajo `src/modulos/menu/`.
 */
export const PANTALLA_DE_TAMANOS: PantallaDeModulo =
  lazy(conRecargaUnica(() => import('./menu/PlantillasDeTamanos.js')))

/**
 * `T-49` · **«Mi cafetería»**, y no es una pantalla de módulo.
 *
 * El tipo lo dice: no recibe `estado`, porque no hay módulo del que sea el
 * estado. `ADR-029`: «se ve siempre, con menú o sin él». Vive aquí igualmente
 * para que su `import()` siga siendo **literal** y `T-23` pueda seguir contando
 * fragmentos — que es el motivo entero de que este archivo exista.
 */
export const PANTALLA_DE_MI_CAFETERIA =
  lazy(conRecargaUnica(() => import('./cafeteria/MiCafeteria.js').then(
    (m) => ({ default: m.MiCafeteria }))))

/**
 * `T-65` · **Personal** y **Aparatos**.
 *
 * Tampoco reciben `estado`: no cuelgan de ningún módulo. Una cafetería que sólo
 * contrató el menú tiene gente que lo edita, y las tablets se revocan el día que
 * se pierden — que no es momento de descubrir que la pantalla depende de una
 * factura.
 *
 * Lo que sí las gobierna es un permiso, y ése lo aplica `SeccionConPermiso`.
 * Viven aquí por lo mismo que «Mi cafetería»: para que su `import()` siga siendo
 * **literal** y `T-23` pueda seguir contando fragmentos.
 */
export const PANTALLA_DE_PERSONAL =
  lazy(conRecargaUnica(() => import('./personal/Personal.js').then(
    (m) => ({ default: m.Personal }))))

export const PANTALLA_DE_APARATOS =
  lazy(conRecargaUnica(() => import('./personal/Aparatos.js').then(
    (m) => ({ default: m.Aparatos }))))

/** La ruta de cada módulo en el panel. Aquí porque el registro es su vecino. */
export const RUTA_DE_MODULO: Record<Modulo, string> = {
  menu: '/menu',
  loyalty: '/lealtad',
}
